import { useMemo, useRef, useState } from 'react';

/**
 * A dropdown you can type into, with the option of adding what you typed.
 *
 * Used where the list belongs to the partner rather than to KAAMWALA - their
 * own catalogue sections, for instance: they pick one they already use, or
 * type a new one and add it on the spot.
 */
const SearchableSelect = ({
  label,
  hint,
  value = '',
  options = [],
  placeholder = 'Search or type a new one',
  emptyText = 'Nothing yet - type to add your first one.',
  allowCreate = true,
  createLabel = (text) => `+ Add "${text}"`,
  onChange,
  onCreate,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return options.filter((o) => !needle || o.toLowerCase().includes(needle));
  }, [options, q]);
  const typed = q.trim();
  const canCreate = allowCreate && typed && !options.some((o) => o.toLowerCase() === typed.toLowerCase());
  const choose = (v) => { onChange(v); setQ(''); setOpen(false); };

  return (
    <div ref={box} style={{ position: 'relative', marginBottom: 14 }} onBlur={(e) => { if (!box.current.contains(e.relatedTarget)) setOpen(false); }}>
      {label ? <label className="field-label">{label}</label> : null}
      {hint ? <div className="t-tiny" style={{ marginBottom: 6 }}>{hint}</div> : null}

      <button
        type="button"
        className="input"
        onClick={() => setOpen((v) => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left', cursor: 'pointer' }}
      >
        <span style={{ color: value ? 'inherit' : 'var(--faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'Choose or add one'}
        </span>
        <span>{open ? '▴' : '▾'}</span>
      </button>

      {open ? (
        <div
          className="card sh-md"
          style={{ position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: 6, padding: 8, maxHeight: 280, overflowY: 'auto' }}
        >
          <input
            className="input"
            autoFocus
            placeholder={placeholder}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          {matches.map((o) => (
            <button
              key={o}
              type="button"
              className={`option-row ${o === value ? 'on' : ''}`}
              onClick={() => choose(o)}
            >
              <span className="grow">{o}</span>
              {o === value ? <span style={{ color: 'var(--primary)', fontWeight: 800 }}>✓</span> : null}
            </button>
          ))}
          {canCreate ? (
            <button
              type="button"
              className="option-row"
              onClick={async () => { if (onCreate) await onCreate(typed); choose(typed); }}
              style={{ color: 'var(--primary)', fontWeight: 700, borderTop: '1px solid var(--line)' }}
            >
              {createLabel(typed)}
            </button>
          ) : null}
          {!matches.length && !canCreate ? <div className="t-tiny" style={{ padding: 8 }}>{emptyText}</div> : null}
        </div>
      ) : null}
    </div>
  );
};

export default SearchableSelect;
